import { useRef } from "preact/hooks";
import { selectPortalAccountScope, selectPortalIdentity } from "../auth/authSelectors";
import { getApiFailureMessage } from "../../lib/gboApiClient";
import { useAppSelector } from "../../store/hooks";
import {
  useGetPortalAccountsQuery,
  useSelectPortalAccountMutation,
} from "../../store/portalAuthApi";
import { portalAccountsView } from "./portalAccountsView";

export function usePortalAccounts() {
  const identity = useAppSelector(selectPortalIdentity);
  const scope = useAppSelector(selectPortalAccountScope);
  const accountsQuery = useGetPortalAccountsQuery(identity?.loginId);
  const [selectAccount, selection] = useSelectPortalAccountMutation();
  const switching = useRef(false);
  const pending = selection.isLoading;
  const selectionError = selection.error
    ? getApiFailureMessage(selection.error, "Unable to switch accounts. Please try again.")
    : null;
  const accounts = accountsQuery.currentData;
  const viewState = portalAccountsView.derive({
    accounts,
    currentCustomerId: identity?.customerId ?? null,
    pending,
    loadError: accountsQuery.isError
      ? getApiFailureMessage(accountsQuery.error, "Please try again.")
      : null,
  });

  async function changeAccount(customerId: string) {
    if (switching.current || customerId === identity?.customerId) return;
    switching.current = true;
    try {
      await selectAccount({ customerId });
    } finally {
      switching.current = false;
    }
  }

  return {
    identity,
    scope,
    accounts,
    pending,
    selectionError,
    viewState,
    busy: pending || accountsQuery.isFetching,
    changeAccount,
    retryAccounts: accountsQuery.refetch,
  };
}
